import { useUntitledUITheme } from '@trm/ui-kit-admin/v7/hooks';
import { ReactNode, useLayoutEffect } from 'react';
import useConfig from '~/hooks/useConfig';
import { ThemeMode } from '~/types/config';

interface ThemeSynchronizerProps {
  children: ReactNode;
}

/**
 * Internal component that synchronizes UntitledUI CSS theme with MUI theme.
 * Must be rendered inside both ThemeCustomization and UntitledUIThemeProvider.
 *
 * Storage Strategy:
 * - MUI theme reads from/writes to localStorage['topstep-dashboard-config']
 * - UntitledUI theme reads from/writes to localStorage['ui-theme']
 * - Both keys persist independently, but this component keeps them in sync
 * - MUI ConfigContext is treated as the source of truth
 * - On mount and when MUI theme changes, we sync UntitledUI to match
 *
 * Uses useLayoutEffect instead of useEffect to ensure synchronous updates
 * before browser paint, preventing visual flashing during theme transitions.
 */
export const ThemeSynchronizer = ({ children }: ThemeSynchronizerProps) => {
  const { mode } = useConfig();
  const { setTheme } = useUntitledUITheme();

  // Sync UntitledUI theme to match MUI theme synchronously before paint
  useLayoutEffect(() => {
    const untitledTheme = mode === ThemeMode.DARK ? 'dark' : 'light';
    setTheme({ theme: untitledTheme });
  }, [mode, setTheme]);

  // Set document color-scheme so native browser controls (e.g. scrollbars) respect
  // dark mode on Windows/Edge, where they are rendered by the OS.
  useLayoutEffect(() => {
    document.documentElement.style.colorScheme = mode === ThemeMode.DARK ? 'dark' : 'light';
  }, [mode]);

  return <>{children}</>;
};
